import { LAYOUT, SANS } from '../level/layout';
import { F, type Level, type Tile } from '../level/types';
import type { Enemy, Particles } from './actors';
import type { Player } from './player';
import { drawRabbit } from './rabbit';
import { TIMED, type World } from './world';

export interface Indicator {
  x: number;
  y: number;
  color: string;
  label: string;
}

export interface Scene {
  level: Level;
  world: World;
  player: Player;
  enemies: Enemy[];
  coinsTaken: Uint8Array;
  particles: Particles;
  time: number;
  checkpoint: number;
  actionTile: Tile | null;
  actionLabel: string | null;
  indicators: Indicator[];
  dive: number;
  hurtFlash: number;
  /** 0 = fully open, 1 = fully closed circle wipe. */
  wipe: number;
  depth: number;
  showPlayer: boolean;
  reducedMotion: boolean;
}

const clamp = (v: number, a: number, b: number) => (v < a ? a : v > b ? b : v);

export class Camera {
  x = 0;
  y = 0;
  zoom = 1;
  viewW = 800;
  viewH = 600;
  shake = 0;

  get left(): number {
    return this.x - this.viewW / 2;
  }
  get top(): number {
    return this.y - this.viewH / 2;
  }

  targetFor(level: Level, px: number, py: number, vy: number): [number, number] {
    const pageW = LAYOUT.pageW;
    let tx: number;
    if (this.viewW >= pageW + 30) tx = pageW / 2;
    else tx = clamp(px, this.viewW / 2 - 16, pageW - this.viewW / 2 + 16);
    const look = vy > 320 ? Math.min(150, (vy - 320) * 0.45) : 0;
    let ty = py - this.viewH * 0.06 + look;
    ty = clamp(ty, this.viewH / 2 - 110, Math.max(this.viewH / 2 - 110, level.height - this.viewH / 2 + 20));
    return [tx, ty];
  }

  follow(tx: number, ty: number, dt: number, snap = false): void {
    if (snap) {
      this.x = tx;
      this.y = ty;
      return;
    }
    this.x += (tx - this.x) * (1 - Math.exp(-dt * 6));
    this.y += (ty - this.y) * (1 - Math.exp(-dt * 7.5));
    this.shake = Math.max(0, this.shake - dt * 2.5);
  }
}

const BG_GLYPHS = '¶§[]{}()†‡*#&?!@~^«»ΔΣΩπλ∞≈√∫'.split('');

export class Renderer {
  readonly ctx: CanvasRenderingContext2D;
  readonly cam = new Camera();
  private dpr = 1;
  private cssW = 0;
  private cssH = 0;
  private visible: Tile[] = [];
  private images = new Map<string, HTMLImageElement>();
  private glyphs = Array.from({ length: 46 }, () => ({
    x: Math.random(),
    y: Math.random(),
    ch: BG_GLYPHS[Math.floor(Math.random() * BG_GLYPHS.length)],
    size: 14 + Math.random() * 30,
    a: 0.04 + Math.random() * 0.08,
    depth: 0.1 + Math.random() * 0.35,
  }));

  constructor(readonly canvas: HTMLCanvasElement) {
    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) throw new Error('Canvas 2D is not supported in this browser.');
    this.ctx = ctx;
    this.resize();
  }

  resize(): void {
    const r = this.canvas.getBoundingClientRect();
    this.cssW = Math.max(1, r.width);
    this.cssH = Math.max(1, r.height);
    this.dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.canvas.width = Math.round(this.cssW * this.dpr);
    this.canvas.height = Math.round(this.cssH * this.dpr);
    let z = this.cssW / (LAYOUT.pageW + 150);
    if (this.cssW < 760) z = Math.max(z, Math.min(1.05, this.cssW / 470));
    z = Math.min(z, this.cssH / 380);
    this.cam.zoom = clamp(z, 0.5, 1.75);
    this.cam.viewW = this.cssW / this.cam.zoom;
    this.cam.viewH = this.cssH / this.cam.zoom;
  }

  worldToScreen(x: number, y: number): [number, number] {
    return [(x - this.cam.left) * this.cam.zoom, (y - this.cam.top) * this.cam.zoom];
  }

  private image(src: string): HTMLImageElement | null {
    let img = this.images.get(src);
    if (!img) {
      img = new Image();
      img.decoding = 'async';
      img.referrerPolicy = 'no-referrer';
      img.src = src;
      this.images.set(src, img);
      if (this.images.size > 160) {
        const first = this.images.keys().next().value as string;
        this.images.delete(first);
      }
    }
    return img.complete && img.naturalWidth > 0 ? img : null;
  }

  draw(s: Scene): void {
    const { ctx, cam } = this;
    const d = this.dpr;
    ctx.setTransform(d, 0, 0, d, 0, 0);
    this.background(s);

    let sx = 0;
    let sy = 0;
    if (cam.shake > 0 && !s.reducedMotion) {
      sx = (Math.random() - 0.5) * cam.shake * 10;
      sy = (Math.random() - 0.5) * cam.shake * 10;
    }
    const z = cam.zoom;
    const left = cam.left + sx;
    const top = cam.top + sy;
    ctx.setTransform(d * z, 0, 0, d * z, -left * d * z, -top * d * z);
    const x1 = left + cam.viewW;
    const y1 = top + cam.viewH;

    this.page(s, top, y1);
    const tiles = s.world.query(left - 40, top - 60, x1 + 40, y1 + 60, this.visible);
    for (const t of tiles) if (t.kind === 'image') this.drawImageTile(t, top, y1);
    this.ladder(s, top, y1);
    this.drawTiles(s, tiles);
    this.decor(s, top, y1);
    this.coins(s, top, y1);
    for (const e of s.enemies) if (e.y > top - 40 && e.y < y1 + 40) this.enemy(e, s.time);
    if (s.showPlayer) this.player(s);
    this.particles(s);
    if (s.actionLabel && s.showPlayer && s.dive <= 0) this.tooltip(s);

    ctx.setTransform(d, 0, 0, d, 0, 0);
    for (const ind of s.indicators) this.indicator(ind, s.time);
    if (s.hurtFlash > 0) {
      ctx.fillStyle = `rgba(215,51,51,${Math.min(0.35, s.hurtFlash)})`;
      ctx.fillRect(0, 0, this.cssW, this.cssH);
    }
    if (s.wipe > 0) this.wipe(s);
  }

  private background(s: Scene): void {
    const { ctx, cssW, cssH, cam } = this;
    const deep = Math.min(1, s.depth / 7);
    const g = ctx.createLinearGradient(0, 0, 0, cssH);
    const mix = (a: number[], b: number[]) => `rgb(${a.map((v, i) => Math.round(v + (b[i] - v) * deep)).join(',')})`;
    g.addColorStop(0, mix([52, 64, 96], [34, 20, 52]));
    g.addColorStop(1, mix([28, 34, 56], [14, 8, 24]));
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, cssW, cssH);
    ctx.fillStyle = '#fff';
    ctx.textAlign = 'center';
    for (const gl of this.glyphs) {
      const px = (((gl.x * cssW * 1.4 - cam.x * gl.depth * cam.zoom) % (cssW * 1.4)) + cssW * 1.4) % (cssW * 1.4) - cssW * 0.2;
      const py = (((gl.y * cssH * 1.5 - cam.y * gl.depth * cam.zoom) % (cssH * 1.5)) + cssH * 1.5) % (cssH * 1.5) - cssH * 0.25;
      ctx.globalAlpha = gl.a;
      ctx.font = `${gl.size}px Georgia, serif`;
      ctx.fillText(gl.ch, px, py);
    }
    ctx.globalAlpha = 1;
    ctx.textAlign = 'left';
  }

  private page(s: Scene, top: number, y1: number): void {
    const { ctx } = this;
    const y0 = Math.max(-120, top - 20);
    const y2 = Math.min(s.level.height, y1 + 20);
    if (y2 <= y0) return;
    ctx.fillStyle = 'rgba(0,0,0,0.25)';
    ctx.fillRect(-6, y0, LAYOUT.pageW + 16, y2 - y0);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, y0, LAYOUT.pageW, y2 - y0);
    // Gutter tint.
    ctx.fillStyle = '#f6f7f9';
    ctx.fillRect(0, y0, LAYOUT.ledgeEnd, y2 - y0);
    for (const p of s.level.panels) {
      if (p.y > y1 || p.y + p.h < top) continue;
      if (p.kind === 'quote') {
        ctx.fillStyle = '#c8ccd1';
        ctx.fillRect(p.x, p.y, p.w, p.h);
        continue;
      }
      ctx.fillStyle = p.kind === 'header' ? '#e5e9f2' : '#f8f9fa';
      ctx.fillRect(p.x, p.y, p.w, p.h);
      if (p.kind !== 'header') {
        ctx.strokeStyle = '#a2a9b1';
        ctx.lineWidth = 1;
        ctx.strokeRect(p.x + 0.5, p.y + 0.5, p.w - 1, p.h - 1);
      }
    }
    ctx.fillStyle = '#a2a9b1';
    for (const r of s.level.rules) if (r.y > top && r.y < y1) ctx.fillRect(r.x, r.y, r.w, 1);
  }

  private ladder(s: Scene, top: number, y1: number): void {
    const { ctx } = this;
    const l = s.level.ladder;
    const a = Math.max(l.y0, top - 20);
    const b = Math.min(l.y1, y1 + 20);
    if (b > a) {
      ctx.fillStyle = '#cdb99a';
      ctx.fillRect(l.x + 3, a, 2.5, b - a);
      ctx.fillRect(l.x + l.w - 5.5, a, 2.5, b - a);
      ctx.fillStyle = '#dccbb0';
      for (let y = Math.ceil(a / 16) * 16; y < b; y += 16) ctx.fillRect(l.x + 4, y, l.w - 8, 2);
    }
    ctx.textAlign = 'center';
    for (const m of s.level.marks) {
      if (m.y < top - 40 || m.y > y1 + 40) continue;
      if (m.kind === 'para') {
        ctx.fillStyle = '#b8bec6';
        ctx.font = `13px Georgia, serif`;
        ctx.fillText('¶', m.x, m.y - 4);
      }
    }
    // Checkpoint flags at each heading.
    for (let i = 1; i < s.level.sections.length; i++) {
      const sec = s.level.sections[i];
      if (sec.y < top - 40 || sec.y > y1 + 40) continue;
      const active = i === s.checkpoint;
      const x = LAYOUT.ledgeEnd - 8;
      ctx.fillStyle = '#72777d';
      ctx.fillRect(x, sec.y - 26, 1.6, 26);
      ctx.fillStyle = active ? '#14866d' : '#c8ccd1';
      ctx.beginPath();
      const wave = active ? Math.sin(s.time * 6) * 1.5 : 0;
      ctx.moveTo(x + 1.6, sec.y - 26);
      ctx.lineTo(x + 13, sec.y - 22 + wave);
      ctx.lineTo(x + 1.6, sec.y - 17);
      ctx.fill();
    }
    ctx.textAlign = 'left';
  }

  private drawImageTile(t: Tile, top: number, y1: number): void {
    const { ctx } = this;
    ctx.fillStyle = '#f8f9fa';
    ctx.fillRect(t.x - 4, t.y - 4, t.w + 8, t.h + 8);
    ctx.strokeStyle = '#c8ccd1';
    ctx.lineWidth = 1;
    ctx.strokeRect(t.x - 3.5, t.y - 3.5, t.w + 7, t.h + 7);
    const near = t.y < y1 + 900 && t.y + t.h > top - 900;
    const img = near && t.image ? this.image(t.image.src) : null;
    if (img) ctx.drawImage(img, t.x, t.y, t.w, t.h);
    else {
      ctx.fillStyle = '#eaecf0';
      ctx.fillRect(t.x, t.y, t.w, t.h);
    }
    // Walkable top edge.
    ctx.fillStyle = '#a2a9b1';
    ctx.fillRect(t.x - 4, t.y - 4, t.w + 8, 2);
  }

  private drawTiles(s: Scene, tiles: Tile[]): void {
    const { ctx } = this;
    const time = s.time;
    let font = '';
    ctx.textBaseline = 'alphabetic';
    for (const t of tiles) {
      if (t.kind === 'image' || t.kind === 'wall') {
        if (t.kind === 'wall' && t.y > 0 && t.x < 0 && t.w > 900) this.floor(t);
        continue;
      }
      if (t.kind === 'ledge') {
        ctx.fillStyle = '#d3d7dd';
        ctx.beginPath();
        ctx.roundRect(t.x, t.y, t.w, 5, 2.5);
        ctx.fill();
        continue;
      }
      const f = t.flags;
      const standing = s.actionTile === t || (s.actionTile && s.actionTile.occ !== undefined && s.actionTile.occ === t.occ);
      let color = t.color ?? (t.kind === 'heading' || t.kind === 'title' ? '#101418' : '#202122');
      let alpha = 1;

      if (t.kind === 'door') {
        const back = t.door?.kind === 'back';
        ctx.fillStyle = standing ? (back ? '#efe7fb' : '#e1ebff') : back ? '#f6f1fc' : '#f1f5ff';
        ctx.strokeStyle = back ? '#8a6bc2' : '#3366cc';
        ctx.lineWidth = 1.2;
        ctx.beginPath();
        ctx.roundRect(t.x, t.y, t.w, t.h, 5);
        ctx.fill();
        ctx.stroke();
        color = back ? '#6b4ba1' : '#2a4b8d';
        if (t.font !== font) ctx.font = font = t.font!;
        ctx.fillStyle = color;
        ctx.fillText(t.text!, t.x + 6, t.y + (t.base ?? 15));
        continue;
      }

      if (f & F.TIMED) {
        const st = s.world.timedState(t);
        if (st === 2) {
          alpha = 0.22;
          ctx.strokeStyle = 'rgba(122,96,196,0.55)';
          ctx.setLineDash([3, 3]);
          ctx.lineWidth = 1;
          ctx.strokeRect(t.x - 1.5, t.y + 0.5, t.w + 3, t.h - 1);
          ctx.setLineDash([]);
        } else {
          const warn = st === 1 && Math.sin(time * 40) > 0;
          ctx.fillStyle = warn ? '#dcd0f7' : '#efe9fb';
          ctx.beginPath();
          ctx.roundRect(t.x - 2, t.y, t.w + 4, t.h, 3);
          ctx.fill();
          const p = (((time / TIMED.period + (t.phase ?? 0)) % 1) + 1) % 1;
          ctx.fillStyle = '#9d86d8';
          ctx.fillRect(t.x - 2, t.y, (t.w + 4) * (1 - p * TIMED.period / (TIMED.on + TIMED.warn)), 2);
        }
      }
      if (f & F.LINK) {
        const exit = f & F.EXIT;
        const hot = f & F.HOT;
        const warm = f & F.WARM;
        let pad = exit ? '#ffd766' : hot ? '#ffd2b3' : warm ? '#fff0bf' : '#e8f1ff';
        if (standing) pad = exit ? '#ffc933' : hot ? '#ffbe94' : warm ? '#ffe594' : '#cfe0ff';
        if (exit) {
          ctx.shadowColor = 'rgba(255,190,40,0.9)';
          ctx.shadowBlur = 10 + Math.sin(time * 5) * 4;
        }
        ctx.fillStyle = pad;
        ctx.beginPath();
        ctx.roundRect(t.x - 2.5, t.y - 1, t.w + 5, t.h + 2, 4);
        ctx.fill();
        ctx.shadowBlur = 0;
        color = exit ? '#5c3b00' : f & F.VISITED ? '#795cb2' : '#3366cc';
        if (hot && !s.reducedMotion && Math.random() < 0.04) {
          s.particles.add({
            x: t.x + Math.random() * t.w,
            y: t.y,
            vx: 0,
            vy: -30,
            life: 0.6,
            size: 2,
            color: 'rgba(255,120,40,0.8)',
            kind: 'flame',
            gravity: -20,
          });
        }
      } else if (f & F.RED) {
        ctx.fillStyle = '#fdeaea';
        ctx.fillRect(t.x - 1, t.y, t.w + 2, t.h);
        ctx.fillStyle = '#d73333';
        ctx.beginPath();
        for (let x = t.x; x < t.x + t.w - 3; x += 7) {
          ctx.moveTo(x, t.y);
          ctx.lineTo(x + 3.5, t.y - 6);
          ctx.lineTo(x + 7, t.y);
        }
        ctx.fill();
        color = '#d73333';
      } else if (t.kind === 'word') {
        if (f & F.SPRING) {
          ctx.strokeStyle = '#ef9a1a';
          ctx.lineWidth = 1.6;
          ctx.beginPath();
          const n = Math.max(2, Math.round(t.w / 7));
          for (let k = 0; k <= n; k++) {
            const x = t.x + (t.w * k) / n;
            const y = t.y + (k % 2 ? 2.5 : -1);
            if (k) ctx.lineTo(x, y);
            else ctx.moveTo(x, y);
          }
          ctx.stroke();
        } else if (f & F.ICE) {
          ctx.fillStyle = '#f1fafd';
          ctx.fillRect(t.x - 1, t.y, t.w + 2, t.h);
          ctx.fillStyle = '#9ad6ee';
          ctx.fillRect(t.x - 1, t.y - 1, t.w + 2, 2);
          ctx.fillStyle = '#ffffff';
          const gx = t.x + ((time * 30 + t.x) % (t.w + 20)) - 10;
          if (gx > t.x && gx < t.x + t.w - 6) ctx.fillRect(gx, t.y - 1, 6, 1);
        } else if (f & F.ONEWAY) {
          ctx.strokeStyle = '#b3b9c1';
          ctx.setLineDash([4, 3]);
          ctx.lineWidth = 1.4;
          ctx.beginPath();
          ctx.moveTo(t.x, t.y + 0.5);
          ctx.lineTo(t.x + t.w, t.y + 0.5);
          ctx.stroke();
          ctx.setLineDash([]);
          alpha *= 0.8;
        } else if (!(f & F.TIMED)) {
          ctx.fillStyle = '#e5e7eb';
          ctx.fillRect(t.x, t.y - 1, t.w, 1.6);
        }
      } else if (t.kind === 'heading' || t.kind === 'title' || t.kind === 'label') {
        ctx.fillStyle = '#e2e5e9';
        ctx.fillRect(t.x, t.y - 1, t.w, 2);
      }
      if (f & F.FOUND) {
        ctx.strokeStyle = `rgba(214,51,132,${0.55 + Math.sin(time * 8) * 0.35})`;
        ctx.lineWidth = 2;
        ctx.strokeRect(t.x - 4, t.y - 3, t.w + 8, t.h + 6);
      }
      if (t.font !== font) ctx.font = font = t.font!;
      ctx.globalAlpha = alpha;
      ctx.fillStyle = color;
      ctx.fillText(t.text!, t.x, t.y + (t.base ?? 16));
      if (f & F.LINK) ctx.fillRect(t.x, t.y + (t.base ?? 16) + 2.5, t.w, standing ? 1.6 : 0.8);
      ctx.globalAlpha = 1;
    }
  }

  private floor(t: Tile): void {
    const { ctx } = this;
    ctx.fillStyle = '#eaecf0';
    ctx.fillRect(0, t.y, LAYOUT.pageW, 300);
    ctx.fillStyle = '#a2a9b1';
    ctx.fillRect(0, t.y, LAYOUT.pageW, 2);
  }

  private decor(s: Scene, top: number, y1: number): void {
    const { ctx } = this;
    for (const d of s.level.decor) {
      if (d.y < top - 20 || d.y > y1 + 40) continue;
      ctx.font = d.font;
      ctx.fillStyle = d.color;
      ctx.textAlign = d.align ?? 'left';
      ctx.fillText(d.text, d.x, d.y);
    }
    ctx.textAlign = 'left';
  }

  private coins(s: Scene, top: number, y1: number): void {
    const { ctx } = this;
    const coins = s.level.coins;
    ctx.font = `700 9px ${SANS}`;
    ctx.textAlign = 'center';
    for (let i = 0; i < coins.length; i++) {
      if (s.coinsTaken[i]) continue;
      const c = coins[i];
      if (c.y < top - 30 || c.y > y1 + 30) continue;
      const bob = s.reducedMotion ? 0 : Math.sin(s.time * 3 + c.x * 0.05) * 2;
      const w = Math.max(15, c.label.length * 5.4 + 8);
      const y = c.y + bob;
      ctx.fillStyle = '#b07d10';
      ctx.beginPath();
      ctx.roundRect(c.x - w / 2, y - 6 + 1.5, w, 13, 4);
      ctx.fill();
      ctx.fillStyle = '#f7c948';
      ctx.beginPath();
      ctx.roundRect(c.x - w / 2, y - 6, w, 12.5, 4);
      ctx.fill();
      ctx.fillStyle = 'rgba(255,255,255,0.6)';
      ctx.fillRect(c.x - w / 2 + 3, y - 4.5, w - 6, 1.5);
      ctx.fillStyle = '#6b4500';
      ctx.fillText(c.label.length > 3 ? c.label.slice(0, 3) : c.label, c.x, y + 3.5);
    }
    ctx.textAlign = 'left';
  }

  private enemy(e: Enemy, time: number): void {
    const { ctx } = this;
    const cx = e.x + e.w / 2;
    const by = e.y + e.h;
    if (!e.alive) {
      if (e.deadT > 0.6) return;
      ctx.globalAlpha = 1 - e.deadT / 0.6;
      ctx.fillStyle = e.kind === 'cn' ? '#6c5ba8' : '#8e2a2a';
      ctx.beginPath();
      ctx.ellipse(cx, by - 1.5, 10 + e.deadT * 10, 2.5, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = 1;
      return;
    }
    const wob = Math.sin(e.phase * 10) * 1.2;
    ctx.save();
    ctx.translate(cx, by);
    if (e.kind === 'cn') {
      ctx.fillStyle = '#6c5ba8';
      ctx.beginPath();
      ctx.moveTo(-8, 0);
      ctx.quadraticCurveTo(-9, -16 + wob, 0, -16);
      ctx.quadraticCurveTo(9, -16 - wob, 8, 0);
      for (let k = 0; k < 4; k++) ctx.lineTo(6 - k * 4.5, k % 2 ? 0 : -3);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = '#fff';
      ctx.font = `700 11px Georgia, serif`;
      ctx.textAlign = 'center';
      ctx.fillText('?', 0, -4);
      ctx.fillStyle = '#6c5ba8';
      ctx.font = `italic 8px ${SANS}`;
      ctx.fillText('[citation needed]', 0, -21 + Math.sin(time * 2) * 1);
    } else {
      ctx.fillStyle = '#9b2c2c';
      ctx.beginPath();
      for (let k = 0; k < 14; k++) {
        const a = (k / 14) * Math.PI * 2;
        const r = 8 + (k % 2 ? 2.5 : 0) + Math.sin(e.phase * 14 + k) * 0.8;
        ctx.lineTo(Math.cos(a) * r, -8 + Math.sin(a) * r);
      }
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = '#fff';
      const look = Math.sign(e.vx) * 1.5;
      ctx.fillRect(-4 + look, -11, 3, 3);
      ctx.fillRect(1 + look, -11, 3, 3);
      ctx.fillStyle = '#111';
      ctx.fillRect(-3 + look * 1.3, -10, 1.5, 1.5);
      ctx.fillRect(2 + look * 1.3, -10, 1.5, 1.5);
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(-4, -5);
      ctx.lineTo(-2, -6.5);
      ctx.lineTo(0, -5);
      ctx.lineTo(2, -6.5);
      ctx.lineTo(4, -5);
      ctx.stroke();
      ctx.textAlign = 'center';
      ctx.font = `italic 8px ${SANS}`;
      ctx.fillStyle = '#9b2c2c';
      ctx.fillText('vandal', 0, -22);
    }
    ctx.restore();
    ctx.textAlign = 'left';
  }

  private player(s: Scene): void {
    const p = s.player;
    const flash = p.invuln > 0 && Math.floor(s.time * 16) % 2 === 0;
    drawRabbit(this.ctx, p.x + p.w / 2, p.y + p.h, {
      facing: p.facing,
      vx: p.vx,
      vy: p.vy,
      onGround: p.onGround,
      climbing: p.climbing,
      runPhase: p.runPhase,
      squash: p.squash,
      time: s.time,
      dive: s.dive,
      flash,
      alpha: 1,
    });
  }

  private particles(s: Scene): void {
    const { ctx } = this;
    for (const p of s.particles.list) {
      const a = Math.max(0, p.life / p.max);
      ctx.globalAlpha = a;
      ctx.fillStyle = p.color;
      if (p.kind === 'text') {
        ctx.font = `700 ${p.size}px ${SANS}`;
        ctx.textAlign = 'center';
        ctx.strokeStyle = 'rgba(255,255,255,0.9)';
        ctx.lineWidth = 3;
        ctx.strokeText(p.text!, p.x, p.y);
        ctx.fillText(p.text!, p.x, p.y);
        ctx.textAlign = 'left';
      } else if (p.kind === 'ring') {
        ctx.strokeStyle = p.color;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size * (1 + (1 - a) * 3), 0, Math.PI * 2);
        ctx.stroke();
      } else if (p.kind === 'spark') {
        const r = p.size;
        ctx.beginPath();
        ctx.moveTo(p.x, p.y - r * 2);
        ctx.lineTo(p.x + r * 0.5, p.y);
        ctx.lineTo(p.x, p.y + r * 2);
        ctx.lineTo(p.x - r * 0.5, p.y);
        ctx.fill();
      } else {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size * (p.kind === 'flame' ? a : 1), 0, Math.PI * 2);
        ctx.fill();
      }
    }
    ctx.globalAlpha = 1;
  }

  private tooltip(s: Scene): void {
    const { ctx } = this;
    const p = s.player;
    const text = s.actionLabel!;
    ctx.font = `600 11.5px ${SANS}`;
    const w = ctx.measureText(text).width + 14;
    const x = Math.max(4, Math.min(LAYOUT.pageW - w - 4, p.x + p.w / 2 - w / 2));
    const y = p.y - 44 + (s.reducedMotion ? 0 : Math.sin(s.time * 4) * 1.5);
    ctx.fillStyle = 'rgba(32,33,34,0.88)';
    ctx.beginPath();
    ctx.roundRect(x, y, w, 19, 9.5);
    ctx.fill();
    ctx.fillStyle = '#fff';
    ctx.fillText(text, x + 7, y + 13.5);
  }

  private indicator(ind: Indicator, time: number): void {
    const { ctx, cssW, cssH } = this;
    const [sx, sy] = this.worldToScreen(ind.x, ind.y);
    const m = 34;
    if (sx > m && sx < cssW - m && sy > m + 50 && sy < cssH - m) return;
    const cx = cssW / 2;
    const cy = cssH / 2;
    const dx = sx - cx;
    const dy = sy - cy;
    const k = Math.min((cssW / 2 - m) / Math.max(1e-3, Math.abs(dx)), (cssH / 2 - m - 26) / Math.max(1e-3, Math.abs(dy)));
    const ex = cx + dx * k;
    const ey = cy + dy * k + 13;
    const ang = Math.atan2(dy, dx);
    const pulse = 1 + Math.sin(time * 5) * 0.08;
    ctx.save();
    ctx.translate(ex, ey);
    ctx.fillStyle = ind.color;
    ctx.strokeStyle = 'rgba(0,0,0,0.35)';
    ctx.lineWidth = 2;
    ctx.save();
    ctx.rotate(ang);
    ctx.scale(pulse, pulse);
    ctx.beginPath();
    ctx.moveTo(14, 0);
    ctx.lineTo(-6, -9);
    ctx.lineTo(-2, 0);
    ctx.lineTo(-6, 9);
    ctx.closePath();
    ctx.stroke();
    ctx.fill();
    ctx.restore();
    ctx.font = `700 11px ${SANS}`;
    ctx.textAlign = 'center';
    const lx = Math.max(-ex + 40, Math.min(cssW - ex - 40, -Math.cos(ang) * 30));
    const ly = -Math.sin(ang) * 22 + 4;
    ctx.lineWidth = 3;
    ctx.strokeStyle = 'rgba(0,0,0,0.6)';
    ctx.strokeText(ind.label, lx, ly);
    ctx.fillText(ind.label, lx, ly);
    ctx.restore();
  }

  private wipe(s: Scene): void {
    const { ctx, cssW, cssH } = this;
    const [px, py] = this.worldToScreen(s.player.x + s.player.w / 2, s.player.y + s.player.h / 2);
    const maxR = Math.hypot(Math.max(px, cssW - px), Math.max(py, cssH - py)) + 20;
    const r = Math.max(0, (1 - s.wipe) * maxR);
    ctx.fillStyle = '#0d0a16';
    ctx.beginPath();
    ctx.rect(0, 0, cssW, cssH);
    if (r > 0.5) ctx.arc(px, py, r, 0, Math.PI * 2, true);
    ctx.fill('evenodd');
  }
}

/** A thin overview strip of the whole article: sections, links, exits, the viewport and the rabbit. */
export class MiniMap {
  private base = document.createElement('canvas');
  private ctx: CanvasRenderingContext2D;
  private level: Level | null = null;
  private w = 0;
  private h = 0;
  private dpr = 1;

  constructor(readonly canvas: HTMLCanvasElement) {
    this.ctx = canvas.getContext('2d')!;
  }

  setLevel(level: Level): void {
    this.level = level;
    this.rebuild();
  }

  rebuild(): void {
    const level = this.level;
    if (!level) return;
    const r = this.canvas.getBoundingClientRect();
    this.w = Math.max(1, r.width);
    this.h = Math.max(1, r.height);
    this.dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.canvas.width = Math.round(this.w * this.dpr);
    this.canvas.height = Math.round(this.h * this.dpr);
    this.base.width = this.canvas.width;
    this.base.height = this.canvas.height;
    const b = this.base.getContext('2d')!;
    b.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    b.fillStyle = 'rgba(255,255,255,0.92)';
    b.fillRect(0, 0, this.w, this.h);
    const sx = this.w / LAYOUT.pageW;
    const sy = this.h / level.height;
    for (const t of level.tiles) {
      if (t.kind === 'wall' || t.kind === 'ledge') continue;
      const hh = Math.max(0.7, t.h * sy);
      if (t.kind === 'image') b.fillStyle = '#c3c8d0';
      else if (t.kind === 'heading' || t.kind === 'title') b.fillStyle = '#202122';
      else if (t.kind === 'door') b.fillStyle = '#8aa6dc';
      else if (t.flags & F.EXIT) b.fillStyle = '#e8a800';
      else if (t.flags & F.LINK) b.fillStyle = '#6f95e0';
      else if (t.flags & F.RED) b.fillStyle = '#e06060';
      else b.fillStyle = '#d4d8de';
      b.fillRect(t.x * sx, t.y * sy, Math.max(0.8, t.w * sx), hh);
    }
  }

  draw(cam: Camera, px: number, py: number, marks: Indicator[], time: number): void {
    const level = this.level;
    if (!level) return;
    const { ctx } = this;
    const d = this.dpr;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.drawImage(this.base, 0, 0);
    ctx.setTransform(d, 0, 0, d, 0, 0);
    const sx = this.w / LAYOUT.pageW;
    const sy = this.h / level.height;
    for (const m of marks) {
      ctx.fillStyle = m.color;
      ctx.beginPath();
      ctx.arc(Math.max(3, Math.min(this.w - 3, m.x * sx)), m.y * sy, 2.6, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.strokeStyle = 'rgba(51,102,204,0.9)';
    ctx.lineWidth = 1.2;
    const vh = Math.max(4, cam.viewH * sy);
    ctx.strokeRect(0.6, cam.top * sy, this.w - 1.2, vh);
    ctx.fillStyle = `rgba(200,50,60,${0.75 + Math.sin(time * 6) * 0.25})`;
    ctx.beginPath();
    ctx.arc(px * sx, py * sy, 3.2, 0, Math.PI * 2);
    ctx.fill();
  }
}
